import { User } from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/lib/firebase'
import { DriverMaster } from '@/types/driverMaster'
import { Earning } from '@/types/earnings'
import { Compensation } from '@/types/compensation'
import { SalaryReportRow } from '@/components/reports/reportConfig'
import { FleetSettings, TipsHandlingMode } from '@/types'
import { userProfileService } from './userProfileService'
import { LockedSalaryDataService } from './lockedSalaryDataService'
import { LockedSalaryData } from '@/types/lockedSalaryData'
import { getAmountToPay } from './lockedDataAdapter'
import { payoutCompanyService } from './payoutCompanyService'

export interface SalarySpecificationData {
  driver: DriverMaster
  period: string
  earnings: Earning[]
  compensations: Compensation[]
  salaryData: SalaryReportRow
  settings: FleetSettings
  feeOnTips?: TipsHandlingMode | boolean
  companyInfo?: {
    name?: string
    orgNumber?: string
    address?: string
    city?: string
    postalCode?: string
  }
  includeSalaryCalculationPage?: boolean  // Include detailed calculation page (page 2), default: true
}

export class SalarySpecificationService {
  /**
   * Prepare data for salary specification PDF generation
   */
  static async prepareSalarySpecificationData(
    user: User,
    driver: DriverMaster,
    period: string,
    earnings: Earning[],
    compensations: Compensation[],
    salaryData: SalaryReportRow,
    settings: FleetSettings
  ): Promise<SalarySpecificationData> {
    // Filter data for the specific driver and period
    const driverEarnings = earnings.filter(earning =>
      earning.driverId === driver.id &&
      (period ? earning.period === period : true)
    )

    const driverCompensations = compensations.filter(comp =>
      comp.driverId === driver.id &&
      (period ? comp.period === period : true)
    )

    // Get company information — use driver's assigned payout company or fall back to UserProfile
    let companyInfo: SalarySpecificationData['companyInfo'] = undefined
    try {
      if (driver.payoutCompanyId) {
        const payoutCompany = await payoutCompanyService.getPayoutCompany(user.uid, driver.payoutCompanyId)
        if (payoutCompany) {
          companyInfo = {
            name: payoutCompany.name,
            orgNumber: payoutCompany.organizationNumber,
            address: payoutCompany.address,
            city: payoutCompany.city,
            postalCode: payoutCompany.postalCode
          }
        }
      }
      if (!companyInfo) {
        const userProfile = await userProfileService.getUserProfile(user.uid)
        if (userProfile) {
          companyInfo = {
            name: userProfile.companyName,
            orgNumber: userProfile.organizationNumber,
            address: userProfile.address,
            city: userProfile.city,
            postalCode: userProfile.postalCode
          }
        }
      }
    } catch (error) {
      console.warn('Could not load company info for salary specification:', error)
    }

    return {
      driver,
      period,
      earnings: driverEarnings,
      compensations: driverCompensations,
      salaryData,
      settings,
      feeOnTips: settings.feeOnTips,
      companyInfo,
      includeSalaryCalculationPage: settings.includeSalaryCalculationPage ?? true
    }
  }

  /**
   * Generate filename for the salary specification PDF
   */
  static generateFilename(driverName: string, period: string, companyName?: string): string {
    const sanitize = (str: string) =>
      str.toLowerCase()
        .replace(/[åä]/g, 'a').replace(/[ö]/g, 'o')
        .replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '')

    const prefix = companyName ? `${sanitize(companyName)}_` : ''
    const formattedPeriod = period.slice(0, 7)

    return `${prefix}lonespecifikation_${sanitize(driverName)}_${formattedPeriod}.pdf`
  }

  /**
   * Format period for display in PDF
   */
  static formatPeriodForDisplay(period: string): string {
    try {
      const date = new Date(period)
      const year = date.getFullYear()
      const month = date.getMonth() + 1

      // Calculate last day of month
      const lastDay = new Date(year, month, 0).getDate()

      const startDate = `${year}-${month.toString().padStart(2, '0')}-01`
      const endDate = `${year}-${month.toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`

      return `${startDate} – ${endDate}`
    } catch (error) {
      console.warn('Error formatting period:', error)
      return period
    }
  }

  /**
   * Validate that all required data is present for PDF generation
   */
  static validateSalarySpecificationData(data: SalarySpecificationData): {
    isValid: boolean
    errors: string[]
  } {
    const errors: string[] = []

    if (!data.driver || !data.driver.id) {
      errors.push('Driver information is missing')
    }

    if (!data.period) {
      errors.push('Period is missing')
    }

    if (!data.salaryData) {
      errors.push('Salary calculation data is missing')
    }

    if (!data.settings) {
      errors.push('Fleet settings are missing')
    }

    if (data.earnings.length === 0 && data.compensations.length === 0) {
      errors.push('No earnings or compensations found for this driver and period')
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  }

  /**
   * Format tax label text for display in salary specification
   * Uses actual tax calculation method and rate from salary data
   */
  static formatTaxLabel(salaryData: SalaryReportRow): string {
    if (salaryData.taxCalculationMethod === 'table') {
      return `Skatteavdrag (Tabell ${salaryData.taxTableUsed || '31'})`
    } else {
      return `Skatteavdrag (${+salaryData.effectiveTaxRate.toFixed(2)}%)`
    }
  }

  /**
   * Transform locked salary data to PDF format
   * Maps LockedSalaryData fields to the PDF data structure
   */
  private static transformLockedDataToPdfFormat(
    locked: LockedSalaryData,
    formattedPeriod: string,
    companyInfo?: SalarySpecificationData['companyInfo'],
    includeSalaryCalculationPage?: boolean
  ) {
    const totals = locked.aggregatedTotals
    const settings = locked.fleetSettingsUsed

    return {
      driver: {
        id: locked.driverId,
        primaryName: locked.driverInfo.name,
        personalCode: locked.driverInfo.personalCode,
        employeeNumber: locked.driverInfo.employeeNumber,
        bankAccount: locked.driverInfo.bankAccount,
        email: locked.driverInfo.email,
      },
      period: formattedPeriod,
      earnings: locked.rawEarnings.map(e => ({
        id: e.earningsId,
        platform: e.platform,
        grossEarnings: e.grossEarnings,
        netEarnings: e.netEarnings,
        tips: e.tips || 0,
        cashReceived: e.cashReceived,
      })),
      compensations: locked.compensations.map(c => ({
        id: c.id,
        type: c.type,
        applyTo: c.applyTo,
        amount: c.amount,
        category: c.category,
        comment: c.comment,
      })),
      salaryData: {
        driverId: locked.driverId,
        driverName: locked.driverInfo.name,
        grossEarnings: totals.grossEarnings,
        netEarnings: totals.netEarnings,
        tips: totals.tips,
        cashReceived: totals.cashReceived,
        vatAmount: totals.vatAmount,
        shareableAmount: totals.shareableAmount,
        afterFleetCommission: totals.afterFleetCommission,
        vacationPay: totals.vacationPay,
        grossSalary: totals.grossSalary,
        finalGrossSalary: totals.finalGrossSalary,
        netSalary: totals.netSalary,
        finalNetSalary: totals.finalNetSalary,
        taxAmount: totals.taxAmount,
        effectiveTaxRate: totals.effectiveTaxRate,
        taxTableUsed: locked.driverTaxSettings?.taxTableName || settings.taxTableName,
        taxColumnUsed: locked.driverTaxSettings?.taxColumn || settings.taxColumn,
        taxCalculationMethod: locked.driverTaxSettings?.taxCalculationMethod || settings.taxCalculationMethod,
        untaxedTips: totals.untaxedTips,
        finalNetPayout: totals.finalNetPayout,
        amountToPay: getAmountToPay(totals),
        netSalaryCompensations: totals.netSalaryCompensations,
        netSalaryDeductions: totals.netSalaryDeductions,
      },
      settings: {
        driverRevenueShare: settings.driverRevenueShare * 100, // Convert decimal to percentage
        vacationPayRate: settings.vacationPayRate * 100,
        taxRate: settings.taxRate * 100,
        vatRate: settings.vatRate * 100,
        feeOnTips: settings.feeOnTips,
        taxCalculationMethod: settings.taxCalculationMethod || 'fixed',
        taxTableName: settings.taxTableName,
        taxColumn: settings.taxColumn,
        employerTaxRate: (settings.employerTaxRate || 0.3142) * 100,
        includeSalaryCalculationPage: includeSalaryCalculationPage ?? true,
      },
      companyInfo,
    }
  }

  /**
   * Transform live salary data to PDF format
   * Used for unlocked periods (preview mode)
   */
  private static transformLiveDataToPdfFormat(
    data: SalarySpecificationData,
    formattedPeriod: string,
    normalizeFeeOnTips: (value?: TipsHandlingMode | boolean) => TipsHandlingMode
  ) {
    return {
      driver: {
        id: data.driver.id,
        primaryName: data.driver.primaryName,
        personalCode: data.driver.personalCode,
        employeeNumber: data.driver.employeeNumber,
        bankAccount: data.driver.bankAccount,
        email: data.driver.email,
      },
      period: formattedPeriod,
      earnings: data.earnings.map(e => ({
        id: e.id,
        platform: e.platform,
        grossEarnings: e.grossEarnings,
        netEarnings: e.netEarnings,
        tips: e.tips || 0,
        cashReceived: e.cashReceived,
      })),
      compensations: data.compensations.map(c => ({
        id: c.id,
        type: c.type,
        applyTo: c.applyTo,
        amount: c.amount,
        category: c.category,
        comment: c.comment,
      })),
      salaryData: {
        driverId: data.salaryData.driverId,
        driverName: data.salaryData.driverName,
        grossEarnings: data.salaryData.grossEarnings,
        netEarnings: data.salaryData.netEarnings,
        tips: data.salaryData.tips,
        cashReceived: data.salaryData.cashReceived,
        vatAmount: data.salaryData.vatAmount,
        shareableAmount: data.salaryData.shareableAmount,
        afterFleetCommission: data.salaryData.afterFleetCommission,
        vacationPay: data.salaryData.vacationPay,
        grossSalary: data.salaryData.grossSalary,
        finalGrossSalary: data.salaryData.finalGrossSalary,
        netSalary: data.salaryData.netSalary,
        finalNetSalary: data.salaryData.finalNetSalary,
        taxAmount: data.salaryData.taxAmount,
        effectiveTaxRate: data.salaryData.effectiveTaxRate,
        taxTableUsed: data.salaryData.taxTableUsed,
        taxColumnUsed: data.salaryData.taxColumnUsed,
        taxCalculationMethod: data.salaryData.taxCalculationMethod,
        untaxedTips: data.salaryData.untaxedTips,
        amountToPay: data.salaryData.amountToPay,
        netSalaryCompensations: data.salaryData.netSalaryCompensations,
        netSalaryDeductions: data.salaryData.netSalaryDeductions,
      },
      settings: {
        driverRevenueShare: data.settings.driverRevenueShare,
        vacationPayRate: data.settings.vacationPayRate,
        taxRate: data.settings.taxRate,
        vatRate: data.settings.vatRate,
        feeOnTips: normalizeFeeOnTips(data.settings.feeOnTips),
        taxCalculationMethod: data.settings.taxCalculationMethod || 'fixed',
        taxTableName: data.settings.taxTableName,
        taxColumn: data.settings.taxColumn,
        employerTaxRate: data.settings.employerTaxRate ?? 31.42,
        includeSalaryCalculationPage: data.includeSalaryCalculationPage ?? true,
      },
      companyInfo: data.companyInfo,
    }
  }

  /**
   * Generate PDF blob from salary specification data using backend Cloud Function
   *
   * This method calls the backend generateSalarySpecificationPdf Cloud Function
   * to ensure consistent PDF generation across all channels (download, email).
   *
   * Data source priority (hybrid approach):
   * 1. If locked data exists for the driver+period: use locked data (immutable snapshot)
   * 2. If no locked data: use live data from the passed SalarySpecificationData (preview)
   *
   * @param user - Firebase user (needed to check for locked data)
   * @param data - Prepared and validated salary specification data (fallback for unlocked periods)
   * @param formattedPeriod - Formatted period string for display (e.g., "2025-01-01 – 2025-01-31" or "Alla perioder")
   * @returns PDF blob ready for download or storage
   */
  static async generatePdfBlob(
    user: User,
    data: SalarySpecificationData,
    formattedPeriod: string
  ): Promise<Blob> {
    // Check for locked data first (single source of truth for locked periods)
    let lockedData: LockedSalaryData | null = null
    if (data.period) {
      try {
        lockedData = await LockedSalaryDataService.getLockedData(user, data.driver.id, data.period)
      } catch (error) {
        console.warn('Could not check for locked data, falling back to live data:', error)
      }
    }
    // Normalize feeOnTips to TipsHandlingMode
    const normalizeFeeOnTips = (value?: TipsHandlingMode | boolean): TipsHandlingMode => {
      if (typeof value === 'boolean') {
        return value ? 'fleet_commission' : 'driver_share'
      }
      return value || 'fleet_commission'
    }

    // Build PDF data from locked data or live data
    const pdfData = lockedData
      ? this.transformLockedDataToPdfFormat(lockedData, formattedPeriod, data.companyInfo, data.includeSalaryCalculationPage)
      : this.transformLiveDataToPdfFormat(data, formattedPeriod, normalizeFeeOnTips)

    // Call backend Cloud Function
    const generatePdf = httpsCallable<
      { driverId: string; period: string; salaryData: typeof pdfData },
      { pdfBase64: string; filename: string }
    >(functions, 'generateSalarySpecificationPdf')

    const result = await generatePdf({
      driverId: data.driver.id,
      period: data.period,
      salaryData: pdfData,
    })

    // Convert base64 to blob
    const binaryString = atob(result.data.pdfBase64)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return new Blob([bytes], { type: 'application/pdf' })
  }
}